import React, { useEffect, useMemo } from "react";
import { RouterProvider, createBrowserRouter, RouteObject } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { Toaster } from "react-hot-toast";
import ReactGA from "react-ga";
import styled from "styled-components";
import ko from "@/i18n/ko";
import en from "@/i18n/en";
import { CM_CTX, DISABLE_GA, GA_TRACKING_KEY } from "@/core/variables";
import { DictType } from "@/model";
import { useProfile } from "@/query/profile";
import { useLocale, useRouteHandler } from "@/hook";
import ErrorBoundaryLayout from "@/component/error/ErrorBoundaryLayout";
import { Error404View } from "@/component/error/ErrorView";
import { Dialog } from "@/component/modal";
import ScenarioContextView from "@/view/ScenarioContextView";
import ScenarioListView from "@/view/scenarios/ScenarioListView";


if (!DISABLE_GA) {
    ReactGA.initialize(GA_TRACKING_KEY!);
}

const RootView: React.FC = () => {
    const { resetRoute } = useRouteHandler();

    useEffect(() => {
        resetRoute();
    }, [resetRoute]);

    return null;
};

const RouterInfo: RouteObject[] = [
    {
        path: "/",
        element: <ErrorBoundaryLayout />,
        children: [
            {
                path: "",
                element: <RootView />
            },
            {
                path: "/scenarios",
                element: <ScenarioListView />
            },
            {
                path: "/scenarios/:scenarioId/*",
                element: <ScenarioContextView />
            },
            {
                path: "*",
                element: <Error404View />
            }
        ]
    }
];

const RouterObject = createBrowserRouter(
    RouterInfo,
    {
        basename: CM_CTX || ""
    }
);

const AppWrapper: React.FC = () => {
    const { locale } = useLocale();

    const messages: DictType<Record<string, string>> = useMemo(() => {
        return { ko: ko, en: en };
    }, []);

    return (
        <IntlProvider locale={locale} messages={messages[locale]}>
            <App />
        </IntlProvider>
    );
};

const App: React.FC = () => {
    const { isPending } = useProfile();

    if (isPending) {
        return null;
    }
    return (
        <div className="scrollbar-track-transparent scrollbar-thumb-black-200 scrollbar-thumb-rounded-full scrollbar-thumb-w-[6px]">
            <AppStyle>
              <RouterProvider router={RouterObject} />
            </AppStyle>
            <Toaster position="top-right"
                     containerStyle={{ top: "15px" }}
                     toastOptions={{
                        style: { maxWidth: "800px" }
                     }}
            />
            <Dialog />
        </div>
    );
};

const AppStyle = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  box-sizing: border-box;
`;

export default AppWrapper;